<template>
  <div class="home-container">
    <el-scrollbar style="height: calc(100vh - 95px); width: 100%">
      <el-row>
        <el-col :span="24 / home.length" v-for="(col, index_c) in home" :key="'c' + index_c" :class="colclass"
          style="padding:7px;">
          <el-row v-for="(row, index_r) in col" :key="'r' + index_r">
            <el-col>
              <div class="widget">
                <div class="widget-header">
                  <div v-if="edit" class="widget-title-edit">
                    <el-input :placeholder="$t('misc.widget_title')" v-model="row.title" size="small">
                      <template #append>
                        <el-button :icon="Setting" @click="openWidgetSettings(index_c, index_r)"
                          :title="$t('misc.widget_settings')" />
                      </template>
                    </el-input>
                  </div>
                  <div v-else class="widget-title-display">
                    <h5>{{ getWidgetTitle(row) }}</h5>
                  </div>
                </div>

                <div v-if="row.type == 'list'">
                  <ListWidget :module="row.module" :view="row.view" />
                </div>
                <div v-if="row.type == 'customlist'">
                  <CustomListWidget :url="row.url" />
                </div>
                <div v-if="row.type == 'legendlist'">
                  <LegendListWidget />
                </div>
                <div v-else-if="row.type == 'linkset'">
                  <LinkSetWidget :links="row.links" />
                  <div v-if="edit" class="widget-actions">
                    <el-button size="small" type="primary" @click="addLink(index_c, index_r)">
                      {{ $t('misc.add_l') }}
                    </el-button>
                  </div>
                </div>
                <div v-else>
                  <div v-if="edit" class="widget-type-selector">
                    <el-space wrap :size="8">
                      <el-button size="small" type="primary" @click="addTable(index_c, index_r)">
                        {{ $t('misc.data') }}
                      </el-button>
                      <el-button size="small" type="success" @click="addCustomTable(index_c, index_r)">
                        {{ $t('misc.custom_d') }}
                      </el-button>
                      <el-button size="small" type="warning" @click="addLegendTable(index_c, index_r)">
                        {{ $t('misc.legend_d') }}
                      </el-button>
                      <el-button size="small" type="info" @click="addNavigation(index_c, index_r)">
                        {{ $t('misc.navigation') }}
                      </el-button>
                      <el-button size="small" type="danger" plain @click="removeRow(index_c, index_r)">
                        {{ $t('misc.remove_w') }}
                      </el-button>
                    </el-space>
                  </div>
                </div>
              </div>

            </el-col>
          </el-row>

          <div v-if="edit" class="column-controls">
            <el-space :size="8">
              <el-button size="small" type="primary" @click="addRow(index_c)">
                {{ $t('misc.add_w') }}
              </el-button>
              <el-button size="small" type="danger" plain @click="removecol(index_c)">
                {{ $t('misc.remove_c') }}
              </el-button>
            </el-space>
          </div>
        </el-col>
      </el-row>
    </el-scrollbar>

    <!-- Edit Mode Toggle and Actions Bar -->
    <div class="edit-mode-bar">
      <div class="edit-mode-toggle">
        <span class="edit-label">{{ $t('misc.edit') }}</span>
        <el-switch v-model="edit" size="large" />
      </div>

      <div v-if="edit" class="edit-mode-actions">
        <el-button v-if="home.length < 4" type="primary" @click="addCol" size="small">
          {{ $t('misc.add_c') }}
        </el-button>
        <el-button type="success" @click="saveLayout" size="small">
          {{ $t('misc.save') }}
        </el-button>
      </div>
    </div>

    <!-- Widget Settings Dialog -->
    <el-dialog v-model="settingsDialogVisible" :title="$t('misc.widget_settings')" width="600px">
      <el-form v-if="currentWidget" label-position="top">
        <el-divider content-position="left">{{ $t('misc.widget_title') }}</el-divider>

        <!-- Multi-language titles -->
        <el-form-item v-for="lang in availableLanguages" :key="lang.code" :label="lang.name">
          <el-input v-model="currentWidgetTitles[lang.code]"
            :placeholder="getDefaultWidgetTitle(currentWidget.type, lang.code)" />
        </el-form-item>

        <el-divider v-if="currentWidget.type === 'list' || currentWidget.type === 'customlist'" content-position="left">
          {{ $t('misc.settings') }}
        </el-divider>

        <el-form-item v-if="currentWidget.type === 'list'" label="Module">
          <el-input v-model="currentWidget.module" disabled />
        </el-form-item>

        <el-form-item v-if="currentWidget.type === 'list'" label="View">
          <el-input v-model="currentWidget.view" disabled />
        </el-form-item>

        <el-form-item v-if="currentWidget.type === 'customlist'" label="URL">
          <el-input v-model="currentWidget.url" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="settingsDialogVisible = false">{{ $t('contextbar.cancel') }}</el-button>
        <el-button type="primary" @click="saveWidgetSettings">{{ $t('contextbar.save') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>


<script setup>
import CustomListWidget from '@/components/widgets/CustomListWidget'
import LegendListWidget from '@/components/widgets/LegendListWidget'
import LinkSetWidget from '@/components/widgets/LinkSetWidget'
import ListWidget from '@/components/widgets/ListWidget'
import { Setting } from '@element-plus/icons-vue'

import { ElMessageBox } from 'element-plus'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from "vue3-i18n"
import { useStore } from "vuex"
import { messages } from '../popups.js'

const store = useStore()
const { t } = useI18n()
let edit = ref(false)
let home = ref([])
let settingsDialogVisible = ref(false)
let currentWidget = ref(null)
let currentWidgetIndices = ref({ col: null, row: null })
let currentWidgetTitles = ref({})

const availableLanguages = [
  { code: 'en', name: 'English' },
  { code: 'lv', name: 'Latviešu' }
]

const { showerorr } = messages()

const getHome = async () => {
  store.$axios.get("/development/dashboard/getLayout", {}).
    then(response => {
      home.value = response.data
    }).
    catch(err => showerorr(err))
}

const getWidgetTitle = (widget) => {
  const currentLocale = store.state.ui?.locale || 'en'

  // Priority 1: Check for multi-language titles (title_lang)
  if (widget.title_lang && widget.title_lang[currentLocale]) {
    return widget.title_lang[currentLocale]
  }

  // Priority 2: Legacy single title field (backward compatibility)
  if (widget.title) {
    return widget.title
  }

  // Priority 3: Use translated default title based on widget type
  if (widget.type && t(`home.widget_titles.${widget.type}`)) {
    return t(`home.widget_titles.${widget.type}`)
  }

  return ''
}

const getDefaultWidgetTitle = (widgetType, locale) => {
  // Get the translated default for the given locale
  const messages = {
    en: {
      list: "Data List",
      customlist: "Custom Data",
      legendlist: "Legend Table",
      linkset: "Quick Links"
    },
    lv: {
      list: "Datu saraksts",
      customlist: "Pielāgoti dati",
      legendlist: "Leģendas tabula",
      linkset: "Ātrās saites"
    }
  }

  return messages[locale]?.[widgetType] || ''
}

const openWidgetSettings = (indexc, indexr) => {
  currentWidget.value = { ...home.value[indexc][indexr] }
  currentWidgetIndices.value = { col: indexc, row: indexr }

  // Initialize title_lang if it doesn't exist
  if (!currentWidget.value.title_lang) {
    currentWidget.value.title_lang = {}
  }

  // Populate currentWidgetTitles with existing values
  currentWidgetTitles.value = {}
  availableLanguages.forEach(lang => {
    currentWidgetTitles.value[lang.code] = currentWidget.value.title_lang[lang.code] || ''
  })

  settingsDialogVisible.value = true
}

const saveWidgetSettings = () => {
  const { col, row } = currentWidgetIndices.value

  // Save title_lang from currentWidgetTitles
  currentWidget.value.title_lang = { ...currentWidgetTitles.value }

  // Keep legacy title field for backward compatibility (use English or first non-empty)
  const legacyTitle = currentWidgetTitles.value.en ||
    currentWidgetTitles.value.lv ||
    Object.values(currentWidgetTitles.value).find(t => t) ||
    currentWidget.value.title
  currentWidget.value.title = legacyTitle

  home.value[col][row] = { ...currentWidget.value }
  settingsDialogVisible.value = false
  currentWidget.value = null
  currentWidgetIndices.value = { col: null, row: null }
  currentWidgetTitles.value = {}
}

const addLink = (indexc, indexr) => {
  ElMessageBox.prompt('Choose function', '', {
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancel',
  })
    .then(({ value }) => {
      home.value[indexc][indexr].links.push({ text: value, url: "/" + value })
    })

}

const addNavigation = (indexc, indexr) => {
  home.value[indexc][indexr] = {
    type: 'linkset',
    links: []
  }

}

const addTable = (indexc, indexr) => {
  ElMessageBox.prompt('Choose view', '', {
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancel',
  })
    .then(({ value }) => {
      store.$axios.post("/development/view/getlistmeta/", { view: value }).
        then(response => {
          // console.log(response)
          home.value[indexc][indexr] = { type: 'list', module: response.data.module, view: response.data.view }
        })

    })
}

const addCustomTable = (indexc, indexr) => {
  ElMessageBox.prompt('URL', '', {
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancel',
  })
    .then(({ value }) => {
      home.value[indexc][indexr] = { type: 'customlist', url: value }

    })
}


const addLegendTable = (indexc, indexr) => {
  home.value[indexc][indexr] = { type: 'legendlist' }
}

const removecol = (indexc) => {
  home.value.splice(indexc, 1)
}
const addCol = () => {
  home.value.push([])
}
const removeRow = (indexc, indexr) => {
  home.value[indexc].splice(indexr, 1)
}
const addRow = (index_c) => {
  home.value[index_c].push({})
}
const saveLayout = () => {
  store.$axios.post("/development/dashboard/saveLayout/", home.value).
    then(() => {
      // console.log(response)
      edit.value = false
    })
}

const colclass = computed(() => edit.value == true ? "bordered" : "")
onMounted(async () => {
  await getHome()
})
</script>


<style scoped>
.widget {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 0 6px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.4);
  border-top: 4px solid #5f7fa9;
  transition: all 0.3s ease;
  margin-bottom: 10px;
}

.widget:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 8px rgba(0, 0, 0, 0.06);
}

.widget-header {
  padding: 10px 15px;
  background: linear-gradient(to bottom, rgba(95, 127, 169, 0.1), transparent);
}

.widget-title-display h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.widget-title-edit {
  margin-bottom: 5px;
}

.widget .el-table {
  border-radius: 3px;
}

.widget-type-selector {
  padding: 20px;
  text-align: center;
}

.widget-actions {
  padding: 10px 15px;
  text-align: center;
  border-top: 1px dashed #e0e0e0;
}

.widget-controls {
  margin-top: 8px;
  text-align: center;
  padding-bottom: 5px;
}

.column-controls {
  margin-top: 15px;
  padding: 10px;
  text-align: center;
  background: rgba(64, 158, 255, 0.05);
  border-radius: 6px;
  border: 2px dashed rgba(64, 158, 255, 0.3);
}

.bordered {
  border: 2px solid rgba(64, 158, 255, 0.3);
  background: rgba(64, 158, 255, 0.02);
}

.edit-mode-bar {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95));
  backdrop-filter: blur(10px);
  padding: 4px 20px;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1);
  border-top: 2px solid rgba(64, 158, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
  height: 26px;
}

.edit-mode-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.edit-label {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
}

.edit-mode-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Smooth transitions for edit mode */
.widget-controls,
.column-controls,
.widget-type-selector,
.widget-actions {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dialog styling */
:deep(.el-dialog) {
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

:deep(.el-dialog__header) {
  background: linear-gradient(135deg, #5f7fa9 0%, #4a6380 100%);
  color: white;
  padding: 20px;
  border-radius: 12px 12px 0 0;
}

:deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
}

:deep(.el-dialog__body) {
  padding: 25px;
}

/* Button enhancements */
:deep(.el-button) {
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
}

:deep(.el-button:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .edit-mode-bar {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .edit-mode-toggle {
    justify-content: center;
  }

  .edit-mode-actions {
    justify-content: center;
    flex-wrap: wrap;
  }

  .widget-type-selector :deep(.el-space) {
    justify-content: center;
  }
}
</style>